import { colorPalette, colorTokens } from '../variables';

export type ButtonRole = 'primary' | 'secondary' | 'tertiary' | 'destructive';

const tokens = colorTokens.button;
const white = colorPalette.neutral100;
const roleStates = {
  primary: {
    color: white,
    background: tokens.navy,
    border: '1px solid transparent',
    hover: tokens.primaryHover,
    pressed: tokens.primaryPressed,
    pressedColor: white,
    disabled: tokens.disabled,
    disabledColor: white,
    disabledBorder: 'transparent',
  },
  secondary: {
    color: tokens.navy,
    background: white,
    border: `2px solid ${tokens.navy}`,
    hover: tokens.hover,
    pressed: tokens.pressed,
    pressedColor: tokens.navy,
    disabled: white,
    disabledColor: tokens.disabled,
    disabledBorder: tokens.disabled,
  },
  tertiary: {
    color: tokens.navy,
    background: 'transparent',
    border: 'none',
    hover: tokens.hover,
    pressed: tokens.pressed,
    pressedColor: tokens.navy,
    disabled: 'transparent',
    disabledColor: tokens.disabled,
    disabledBorder: 'transparent',
  },
  destructive: {
    color: tokens.destructive,
    background: 'transparent',
    border: 'none',
    hover: tokens.destructiveHover,
    pressed: tokens.destructiveMuted,
    pressedColor: tokens.destructivePressed,
    disabled: 'transparent',
    disabledColor: tokens.destructiveMuted,
    disabledBorder: 'transparent',
  },
};

export const getRoleButtonSx = (role: ButtonRole) => {
  const state = roleStates[role];
  const hasFocusGap = role === 'primary' || role === 'secondary';

  return {
    '&&': {
      color: state.color,
      backgroundColor: state.background,
      border: state.border,
      boxShadow: 'none',
      // ButtonGroup's legacy pointer-focus rule must not override keyboard-only focus.
      outline: 'none !important',
      outlineOffset: hasFocusGap ? '2px' : 0,
      '&:focus': { outline: 'none !important' },
      '& svg': { color: 'inherit' },
      '&:hover, &.-hover': { color: state.color, backgroundColor: state.hover, boxShadow: 'none' },
      '&:active, &.-active': { color: state.pressedColor, backgroundColor: state.pressed, boxShadow: 'none' },
      '&:focus-visible, &.Mui-focusVisible': {
        outline: '2px solid transparent !important',
        boxShadow: hasFocusGap ? `0 0 0 2px ${white}, 0 0 0 4px ${tokens.navy}` : `0 0 0 2px ${tokens.navy}`,
        '@media (forced-colors: active)': { outlineColor: 'ButtonText !important' },
      },
      '&.Mui-disabled': {
        color: state.disabledColor,
        backgroundColor: state.disabled,
        borderColor: state.disabledBorder,
        boxShadow: 'none',
      },
      '&.MuiButton-sizeLarge': { fontSize: '15px', height: '38px' },
      '&.MuiButton-sizeSmall': { fontSize: '13px', height: '30px' },
    },
  };
};

export const StyledButtonSx = [
  {
    color: colorTokens.primary.main,
    backgroundColor: colorTokens.secondary.dark,
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: 'none',
    },
    '&:active,focus': {
      outline: 'none',
    },
    '&.MuiButton-contained.Mui-disabled': {
      backgroundColor: colorTokens.text.disabled,
      border: colorPalette.neutralvariant70,
      color: colorTokens.text.contrast,
    },
    '&.MuiButton-contained': {
      color: colorTokens.text.contrast,
      '&:hover': {
        backgroundColor: colorTokens.text.hover,
        border: `1px solid ${colorTokens.text.hover}`,
        boxShadow: 'none',
      },
      '&:focus': {
        border: `1px solid ${colorTokens.text.contrast}`,
        boxShadow: 'none',
        outline: `2px solid ${colorTokens.text.hover}`,
      },
      '&:active': {
        backgroundColor: colorTokens.primary.dark,
        border: `1px solid ${colorTokens.primary.dark}`,
        boxShadow: 'none',
      },
      '&:active,focus': {
        outline: 'none',
      },
      '&.Mui-disabled': {
        backgroundColor: colorTokens.text.disabled,
        color: colorTokens.text.contrast,
        border: 'none',
      },
    },
    '&.MuiButton-text': {
      backgroundColor: 'transparent',
      '&:hover': {
        backgroundColor: 'transparent',
        color: colorTokens.text.hover,
      },
      '&:focus': {
        boxShadow: 'none',
        outline: `2px solid ${colorTokens.text.hover}`,
      },
      '&:active': {
        color: colorTokens.primary.dark,
      },
      '&:active,focus': {
        outline: 'none',
      },
      '&.Mui-disabled': {
        color: colorTokens.text.disabled,
        border: 'none',
      },
    },
    '&.MuiButton-containedPrimary': {
      boxShadow: 'none',
      backgroundColor: colorTokens.primary.main,
      border: `1px solid ${colorTokens.primary.main}`,
      '&:hover': {
        backgroundColor: colorTokens.text.hover,
        border: `1px solid ${colorTokens.text.hover}`,
        boxShadow: 'none',
      },
      '&:focus': {
        border: `1px solid ${colorTokens.text.contrast}`,
        boxShadow: 'none',
        outline: `2px solid ${colorTokens.text.hover}`,
      },
      '&:active': {
        backgroundColor: colorTokens.primary.dark,
        border: `1px solid ${colorTokens.primary.dark}`,
        boxShadow: 'none',
      },
      '&:active,focus': {
        outline: 'none',
      },
    },
    '&.MuiButton-containedSecondary': {
      backgroundColor: colorTokens.secondary.dark,
      border: `1px solid ${colorTokens.secondary.dark}`,
      boxShadow: 'none',
      '&:hover': {
        backgroundColor: colorPalette.blue70,
        border: `1px solid ${colorPalette.blue70}`,
        boxShadow: 'none',
      },
      '&:focus': {
        border: `1px solid ${colorTokens.text.contrast}`,
        boxShadow: 'none',
        outline: `2px solid ${colorTokens.text.hover}`,
      },
      '&:active': {
        backgroundColor: colorPalette.blue40,
        border: `1px solid ${colorPalette.blue40}`,
        boxShadow: 'none',
        outline: 'none',
      },
      '&:active,focus': {
        outline: 'none',
      },
    },
    '&.MuiButton-sizeLarge': {
      fontSize: '15px',
      height: '38px',
    },
    '&.MuiButton-sizeSmall': {
      fontSize: '13px',
      height: '30px',
    },
  },
];

export const TonalButtonSx = [
  {
    '&.MuiButton-contained': {
      color: colorTokens.primary.main,
      backgroundColor: colorTokens.text.contrast,
      boxShadow: 'none',
      '&:hover': {
        color: colorTokens.text.hover,
        backgroundColor: colorTokens.text.contrast,
        boxShadow: 'none',
      },
      '&:focus': {
        boxShadow: 'none',
        outline: `2px solid ${colorTokens.text.hover}`,
      },
      '&:active': {
        color: colorTokens.primary.dark,
      },
      '&:active,focus': {
        outline: 'none',
      },
      '&.Mui-disabled': {
        backgroundColor: colorPalette.neutral95,
        color: colorPalette.neutral50,
      },
    },
  },
];

export const TonalVariantButtonSx = [
  {
    '&.MuiButton-contained': {
      color: colorTokens.primary.main,
      backgroundColor: colorTokens.background.light,
      boxShadow: 'none',
      '&:hover': {
        color: colorTokens.primary.light,
        backgroundColor: colorTokens.background.light,
        boxShadow: 'none',
      },
      '&:focus': {
        boxShadow: 'none',
        outline: `2px solid ${colorTokens.text.hover}`,
      },
      '&:active': {
        color: colorTokens.primary.dark,
      },
      '&:active,focus': {
        outline: 'none',
      },
      '&.Mui-disabled': {
        backgroundColor: colorPalette.neutral95,
        color: colorPalette.neutral50,
      },
    },
  },
];

export const WArningButtonSx = [
  {
    '&.MuiButton-contained': {
      color: colorPalette.red30,
      backgroundColor: colorPalette.red90,
      boxShadow: 'none',
      '&:hover': {
        color: colorPalette.red43,
        backgroundColor: colorPalette.red95,
        boxShadow: 'none',
      },
      '&:focus': {
        boxShadow: 'none',
        border: `2px solid ${colorPalette.navy50}`,
        outline: `2px solid ${colorTokens.text.contrast}`,
      },
      '&:active': {
        color: colorPalette.red30,
        backgroundColor: colorPalette.red80,
      },
      '&:active,focus': {
        outline: 'none',
      },
      '&.Mui-disabled': {
        backgroundColor: colorPalette.neutral95,
        color: colorPalette.neutral50,
      },
    },
  },
];
